import { useEffect, useRef } from 'react';
import '../cursor.css';

const TEXT_SELECTOR = 'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]), textarea, [contenteditable="true"], .ProseMirror';
const HOVER_SELECTOR = 'a, button, [role="button"], label, select, summary';

export default function CustomCursor() {
  const cursorRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    if (!window.matchMedia('(any-pointer: fine)').matches) return undefined;

    const el = cursorRef.current;
    if (!el) return undefined;

    const root = document.documentElement;
    root.classList.add('has-custom-cursor');

    let targetX = -100;
    let targetY = -100;
    let currentX = -100;
    let currentY = -100;
    let visible = false;
    let rafId = 0;

    const updateMode = (target) => {
      if (!target || typeof target.closest !== 'function') {
        el.classList.remove('is-hover', 'is-text');
        return;
      }
      if (target.closest(TEXT_SELECTOR)) {
        el.classList.add('is-text');
        el.classList.remove('is-hover');
      } else if (target.closest(HOVER_SELECTOR)) {
        el.classList.add('is-hover');
        el.classList.remove('is-text');
      } else {
        el.classList.remove('is-hover', 'is-text');
      }
    };

    const tick = () => {
      currentX += (targetX - currentX) * 0.32;
      currentY += (targetY - currentY) * 0.32;
      el.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
      if (Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = 0;
      }
    };

    const onMove = (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!visible) {
        currentX = targetX;
        currentY = targetY;
        visible = true;
        el.classList.add('is-visible');
      }
      updateMode(e.target);
      if (!rafId) rafId = window.requestAnimationFrame(tick);
    };

    const onDown = () => el.classList.add('is-pressed');
    const onUp = () => el.classList.remove('is-pressed');
    const onLeave = () => {
      visible = false;
      el.classList.remove('is-visible', 'is-pressed');
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('blur', onLeave);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      root.classList.remove('has-custom-cursor');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('blur', onLeave);
    };
  }, []);

  return (
    <div ref={cursorRef} className="liminal-cursor" aria-hidden="true">
      <div className="liminal-cursor-disc" />
    </div>
  );
}
